/* eslint-disable react/require-default-props */
import React, {Children} from 'react';
import {Image, View} from 'react-native';
import {flow, get, isNumber, map} from 'lodash/fp';

import FormattedNumber from '/component/base/FormattedNumber';
import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import ScreenSection from '/component/layout/ScreenSection';
import {OrderDetails} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {generateComponentCallback} from '/util/component.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {openURL} from '/util/linking.util';

import {styles as sectionStyles} from '../Section.style';

import {styles} from './ShipmentSection.style';

const BOLD_TAG_PATTERN = /<b>(.*?)<\/b>/gi;

const formatVolume = (value?: string | number) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return numericValue.toFixed(2).replace(/\.?0+$/, '');
};

const stripBoldTags = (value = '') => value.replace(/<\/?b>/gi, '');

const renderTitleWithBoldText = (title = '') => {
  const segments: React.ReactNode[] = [];
  let match: RegExpExecArray | null = null;
  let previousIndex = 0;
  let key = 0;

  while ((match = BOLD_TAG_PATTERN.exec(title)) !== null) {
    if (match.index > previousIndex) {
      segments.push(title.slice(previousIndex, match.index));
    }

    segments.push(
      <Text
        key={`item-title-bold-${key}`}
        variant="button"
        isBold
        accessible={false}>
        {match[1]}
      </Text>,
    );
    previousIndex = BOLD_TAG_PATTERN.lastIndex;
    key += 1;
  }

  if (previousIndex < title.length) {
    segments.push(title.slice(previousIndex));
  }

  BOLD_TAG_PATTERN.lastIndex = 0;

  if (!segments.length) {
    return stripBoldTags(title);
  }

  return segments;
};

interface Item {
  imageUrl?: string;
  title: string;
  quantity: number;
  servings?: any;
  price: string | number;
  currencyCode: string;
  cv?: string | number;
  qv?: string | number;
}

interface ShipmentSectionProps {
  tt?: (key: any, value: any) => any;
  trackingNumber?: string;
  trackingUrl?: string;
  items?: Item[];
  featureFlag?: Record<string, any>;
}

export const ShipmentSection: React.FC<ShipmentSectionProps> = ({
  tt = withTranslation.ttDefaultProp,
  trackingNumber = '',
  trackingUrl = '',
  items = [],
  featureFlag = {},
}: ShipmentSectionProps) => {
  const shouldShowNewOrderHistory = shouldReleaseFeature(
    get('ff_order_history', featureFlag),
  );
  const trackingDisplayValue =
    trackingNumber?.trim() ||
    tt('trackingNumber.unavailable', 'Tracking unavailable');
  const hasTrackingLink = Boolean(
    trackingNumber?.trim() && trackingUrl?.trim(),
  );
  const renderItem = (item: Item, index: number) => {
    const plainItemTitle = stripBoldTags(item.title);
    const formattedQv = formatVolume(item.qv);
    const formattedCv = formatVolume(item.cv);
    const qvDisplayValue =
      formattedQv ?? (shouldShowNewOrderHistory ? '-' : '—');
    const cvDisplayValue =
      formattedCv ?? (shouldShowNewOrderHistory ? '-' : '—');
    const hasVolumeValues = formattedQv !== null || formattedCv !== null;
    const hasServingsValue = isNumber(item.servings);
    const servingsDisplayValue = hasServingsValue
      ? item.servings
      : shouldShowNewOrderHistory
      ? '-'
      : '—';
    const servingsText = isNumber(item.servings)
      ? `, ${item.servings} servings per kit`
      : '';
    const qvCvText =
      shouldShowNewOrderHistory || hasVolumeValues
        ? `. QV/CV: ${qvDisplayValue} / ${cvDisplayValue}`
        : '';
    const itemLabel = `${plainItemTitle}, Quantity: ${item.quantity}${servingsText}. Price: ${item.price} ${item.currencyCode}${qvCvText}`;
    const shouldRenderServingsRow =
      shouldShowNewOrderHistory || hasServingsValue || hasVolumeValues;

    return (
      <ListItem
        testID={`${OrderDetails.shipmentItemPrefix}-${index}`}
        readonly
        style={{base: styles.item}}
        accessible={false}
        accessibilityLabel={itemLabel}
        prefix={
          <Image
            testID={`${OrderDetails.shipmentItemImagePrefix}-${index}`}
            style={styles.itemImage}
            source={{uri: item.imageUrl}}
            accessible={false}
          />
        }
        label={
          <View style={styles.itemContent} accessible={false}>
            <View style={styles.itemHeaderRow} accessible={false}>
              <Text
                variant="button"
                style={styles.itemTitle}
                testID={`${OrderDetails.shipmentItemProductNamePrefix}-${index}`}
                accessible={true}>
                {renderTitleWithBoldText(item.title)}
              </Text>
              <FormattedNumber
                testID={`${OrderDetails.shipmentItemPricePrefix}-${index}`}
                value={Number(item.price)}
                currencyCode={item.currencyCode}
                separationStyle="thousand"
                decimalPlaces={2}
                accessible={true}
              />
            </View>
            <View style={styles.itemMetaRow} accessible={false}>
              {tt(
                'item.qty',
                generateComponentCallback({
                  component: (
                    <>
                      <Text
                        emphasis="low"
                        testID={`${OrderDetails.shipmentItemQuantityLabelPrefix}-${index}`}
                        accessible={true}>
                        Qty:
                      </Text>
                      <Text
                        emphasis="low"
                        testID={`${OrderDetails.shipmentItemQuantityPrefix}-${index}`}
                        accessible={true}>
                        {item.quantity}
                      </Text>
                    </>
                  ),
                }),
              )}
              {shouldShowNewOrderHistory || hasVolumeValues ? (
                <View
                  style={styles.itemVolumeContainer}
                  accessible={false}
                  testID={`${OrderDetails.shipmentItemQvCvPrefix}-${index}`}>
                  <Text
                    emphasis="low"
                    testID={`${OrderDetails.shipmentItemQvLabelPrefix}-${index}`}
                    accessible={true}>
                    QV
                  </Text>
                  <Text emphasis="low" accessible={false}>
                    /
                  </Text>
                  <Text
                    emphasis="low"
                    testID={`${OrderDetails.shipmentItemCvLabelPrefix}-${index}`}
                    accessible={true}>
                    CV
                  </Text>
                </View>
              ) : null}
            </View>
            {shouldRenderServingsRow ? (
              <View style={styles.itemMetaRow} accessible={false}>
                {tt('item.servings', () => (
                  <>
                    <Text
                      emphasis="low"
                      testID={`${OrderDetails.shipmentItemServingsLabelPrefix}-${index}`}
                      accessible={true}>
                      Servings Per Kit:
                    </Text>
                    <Text
                      emphasis="low"
                      testID={`${OrderDetails.shipmentItemServingsPrefix}-${index}`}
                      accessible={true}>
                      {servingsDisplayValue}
                    </Text>
                  </>
                ))}
                {shouldShowNewOrderHistory || hasVolumeValues ? (
                  <View
                    style={styles.itemVolumeContainer}
                    accessible={false}
                    testID={`${OrderDetails.shipmentItemQvCvPrefix}-${index}-value`}>
                    <Text
                      emphasis="low"
                      testID={`${OrderDetails.shipmentItemQvValuePrefix}-${index}`}
                      accessible={true}>
                      {qvDisplayValue}
                    </Text>
                    <Text emphasis="low" accessible={false}>
                      /
                    </Text>
                    <Text
                      emphasis="low"
                      testID={`${OrderDetails.shipmentItemCvValuePrefix}-${index}`}
                      accessible={true}>
                      {cvDisplayValue}
                    </Text>
                  </View>
                ) : null}
              </View>
            ) : null}
          </View>
        }
      />
    );
  };

  return (
    <ScreenSection
      style={sectionStyles}
      title={generateComponentCallback({
        component: (
          <Gutter>
            <Text
              variant="subheading"
              emphasis="low"
              testID={OrderDetails.shipmentSectionHeading}
              accessible={true}
              accessibilityRole="header">
              {tt('section.items.heading', 'Shipment Details')}
            </Text>
          </Gutter>
        ),
      })}>
      <Gutter>
        <ListItem
          testID={OrderDetails.trackingId}
          readonly
          accessible={false}
          accessibilityLabel={`${tt(
            'trackingNumber.label',
            'Tracking ID',
          )}: ${trackingDisplayValue}`}
          label={
            <Text
              testID={OrderDetails.shipmentTrackingIdLabel}
              accessible={true}>
              {tt('trackingNumber.label', 'Tracking ID')}
            </Text>
          }
          suffix={
            hasTrackingLink ? (
              <TouchableOpacity
                onPress={() => openURL('url', trackingUrl)}
                testID={OrderDetails.trackingIdLink}
                accessibilityRole="link"
                accessibilityLabel={trackingDisplayValue}>
                <Text
                  variant="button"
                  testID={OrderDetails.shipmentTrackingIdValue}
                  style={styles.trackingLinkText}
                  accessible={true}>
                  {trackingDisplayValue}
                </Text>
              </TouchableOpacity>
            ) : (
              <Text
                variant="button"
                testID={OrderDetails.shipmentTrackingIdValue}
                accessible={true}>
                {trackingDisplayValue}
              </Text>
            )
          }
        />
        {flow([map.convert({cap: false})(renderItem), Children.toArray])(items)}
      </Gutter>
    </ScreenSection>
  );
};

export default withTranslation('screen.orderDetail.section.shipment')(
  ShipmentSection,
);
